// ═══════════════════════════════════════════════════════════════
// VolunteerOS — Settings
// Organization settings, check-in config, thresholds, admin mgmt
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import {
  Title, Text, Paper, Stack, Tabs, TextInput, NumberInput, Switch,
  Group,
} from '../../components/gab/GabComponents';
import {
  IconBuilding, IconLogin, IconGauge, IconBell,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Types ────────────────────────────────────────────────────

interface OrgSettings {
  orgName: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
  address: string;
}

interface CheckInSettings {
  selfCheckin: boolean;
  requireGeo: boolean;
  autoCheckOut: boolean;
  geoRadius: number;
  windowMinutes: number;
}

interface ThresholdSettings {
  minRestHours: number;
  maxConsecutiveDays: number;
}

interface NotificationSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
}

interface AllSettings {
  org: OrgSettings;
  checkIn: CheckInSettings;
  thresholds: ThresholdSettings;
  notifications: NotificationSettings;
}

const LS_KEY = 'vol-org-settings';

const DEFAULTS: AllSettings = {
  org: { orgName: '', contactEmail: '', contactPhone: '', website: '', address: '' },
  checkIn: { selfCheckin: true, requireGeo: false, autoCheckOut: false, geoRadius: 100, windowMinutes: 15 },
  thresholds: { minRestHours: 8, maxConsecutiveDays: 7 },
  notifications: { emailEnabled: true, smsEnabled: false },
};

function loadSettings(): AllSettings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AllSettings>;
      return {
        org: { ...DEFAULTS.org, ...parsed.org },
        checkIn: { ...DEFAULTS.checkIn, ...parsed.checkIn },
        thresholds: { ...DEFAULTS.thresholds, ...parsed.thresholds },
        notifications: { ...DEFAULTS.notifications, ...parsed.notifications },
      };
    }
  } catch { /* ignore */ }
  return { ...DEFAULTS };
}

function persistSettings(settings: AllSettings): void {
  localStorage.setItem(LS_KEY, JSON.stringify(settings));
}

// ── Component ────────────────────────────────────────────────

export default function Settings() {
  useModulePermission('volunteer');

  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState<AllSettings>(() => loadSettings());
  useEffect(() => {
    if (loaded) return;
    setLoaded(true);
  }, [loaded]);

  // Persist on every change
  const update = useCallback(<K extends keyof AllSettings>(section: K, patch: Partial<AllSettings[K]>) => {
    setSettings(prev => {
      const next = {
        ...prev,
        [section]: { ...prev[section], ...patch },
      };
      persistSettings(next);
      return next;
    });
  }, []);

  // ── Render ────────────────────────────────────────────────

  const { org, checkIn, thresholds, notifications: notifSettings } = settings;

  return (
    <Stack gap="lg">
      <Title order={3} c="black">Settings</Title>

      <Paper p="md" radius="sm" withBorder>
        <Tabs defaultValue="org">
          <Tabs.List>
            <Tabs.Tab value="org" leftSection={<IconBuilding size={14} />}>Organization</Tabs.Tab>
            <Tabs.Tab value="checkin" leftSection={<IconLogin size={14} />}>Check-In</Tabs.Tab>
            <Tabs.Tab value="thresholds" leftSection={<IconGauge size={14} />}>Thresholds</Tabs.Tab>
            <Tabs.Tab value="notif" leftSection={<IconBell size={14} />}>Notifications</Tabs.Tab>
          </Tabs.List>

          {/* Organization */}
          <Tabs.Panel value="org" pt="md">
            <Stack gap="sm">
              <TextInput
                label="Organization Name"
                placeholder="Your organization"
                value={org.orgName}
                onChange={e => update('org', { orgName: e.target.value })}
              />
              <TextInput
                label="Contact Email"
                placeholder="contact@example.org"
                value={org.contactEmail}
                onChange={e => update('org', { contactEmail: e.target.value })}
              />
              <TextInput
                label="Contact Phone"
                placeholder="(555) 555-5555"
                value={org.contactPhone}
                onChange={e => update('org', { contactPhone: e.target.value })}
              />
              <TextInput
                label="Website"
                placeholder="https://example.org"
                value={org.website}
                onChange={e => update('org', { website: e.target.value })}
              />
              <TextInput
                label="Address"
                placeholder="123 Main St, City, ST 00000"
                value={org.address}
                onChange={e => update('org', { address: e.target.value })}
              />
              <Text size="xs" c="dimmed">Settings are saved automatically.</Text>
            </Stack>
          </Tabs.Panel>

          {/* Check-In */}
          <Tabs.Panel value="checkin" pt="md">
            <Stack gap="sm">
              <Switch
                label="Self Check-In"
                description="Allow volunteers to check themselves in"
                checked={checkIn.selfCheckin}
                onChange={e => update('checkIn', { selfCheckin: e.currentTarget.checked })}
              />
              <Switch
                label="Require Geolocation"
                description="Require GPS verification at check-in"
                checked={checkIn.requireGeo}
                onChange={e => update('checkIn', { requireGeo: e.currentTarget.checked })}
              />
              <Switch
                label="Auto Check-Out"
                description="Automatically check out at shift end"
                checked={checkIn.autoCheckOut}
                onChange={e => update('checkIn', { autoCheckOut: e.currentTarget.checked })}
              />
              <NumberInput
                label="Geofence Radius (meters)"
                description="Maximum distance from site for check-in"
                value={checkIn.geoRadius}
                onChange={v => update('checkIn', { geoRadius: typeof v === 'number' ? v : 100 })}
                min={10}
                max={5000}
                step={10}
              />
              <NumberInput
                label="Check-In Window (minutes)"
                description="How early/late a volunteer can check in"
                value={checkIn.windowMinutes}
                onChange={v => update('checkIn', { windowMinutes: typeof v === 'number' ? v : 15 })}
                min={5}
                max={120}
                step={5}
              />
              <Text size="xs" c="dimmed">Settings are saved automatically.</Text>
            </Stack>
          </Tabs.Panel>

          {/* Thresholds */}
          <Tabs.Panel value="thresholds" pt="md">
            <Stack gap="sm">
              <NumberInput
                label="Minimum Rest Hours"
                description="Required hours between shifts"
                value={thresholds.minRestHours}
                onChange={v => update('thresholds', { minRestHours: typeof v === 'number' ? v : 8 })}
                min={1}
                max={48}
              />
              <NumberInput
                label="Max Consecutive Days"
                description="Maximum days a volunteer can work in a row"
                value={thresholds.maxConsecutiveDays}
                onChange={v => update('thresholds', { maxConsecutiveDays: typeof v === 'number' ? v : 7 })}
                min={1}
                max={30}
              />
              <Text size="xs" c="dimmed">Settings are saved automatically.</Text>
            </Stack>
          </Tabs.Panel>

          {/* Notifications */}
          <Tabs.Panel value="notif" pt="md">
            <Stack gap="sm">
              <Switch
                label="Email Notifications"
                description="Enable email delivery for broadcasts"
                checked={notifSettings.emailEnabled}
                onChange={e => update('notifications', { emailEnabled: e.currentTarget.checked })}
              />
              <Switch
                label="SMS Notifications"
                description="Enable SMS delivery for broadcasts"
                checked={notifSettings.smsEnabled}
                onChange={e => update('notifications', { smsEnabled: e.currentTarget.checked })}
              />
              <Paper p="sm" radius="sm" bg="rgba(75,63,255,0.1)">
                <Text size="xs">
                  Email and SMS delivery is handled by the OpenGov Worker proxy using AWS SES and SNS.
                  These toggles control whether the Comms page offers those channels by default.
                  Actual delivery configuration (verified sender, SNS topics) is managed at the worker level.
                </Text>
              </Paper>
              <Text size="xs" c="dimmed">Settings are saved automatically.</Text>
            </Stack>
          </Tabs.Panel>

        </Tabs>
      </Paper>
    </Stack>
  );
}
